/**
 * The frozen Plan Version document — its shape, its guard, and its label (RP-8.1, M4).
 *
 * Pure: no I/O, no db, no permissions. `src/api/plan-versions.ts` writes and reads it.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * WHAT A SNAPSHOT ACTUALLY HAS TO CONTAIN
 * ──────────────────────────────────────────────────────────────────────────────
 * RP-8.1: "Freezing copies plan content, applied rates, and margin results into a
 * read-only version." Three things, and the third is the one that makes the document
 * necessary rather than merely convenient: a frozen version CANNOT re-derive its margin,
 * because the Rate Card, the Labor Category costs, the margin floor and the planning
 * constants it was built on are all free to move afterwards (non-negotiable 8, RP-14.1).
 *
 * So the document stores the plan's own detail view verbatim — content AND every derived
 * figure — which is exactly §13's named exception to "derived, never stored".
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * WHY IT STORES THE COST-VISIBLE VIEW
 * ──────────────────────────────────────────────────────────────────────────────
 * The document holds the `canViewCost: true` view. Not because cost is safe here — it is
 * not — but because a version that dropped cost at FREEZE time could never answer
 * RP-10.2's internal export or RP-7.2's "Revenue, Cost, Net, Margin % at every grain" for
 * a plan that has already been sent, and re-deriving it later would reprice the version
 * against today's costs. The freeze is the only moment those figures are true.
 *
 * 🔒 The document is therefore NEVER returned raw. `shapeVersionForActor` in
 * src/api/plan-versions.ts constructs the response per audience, and the summary columns
 * on `resource_plan_version` are cost-free so the versions LIST never touches the
 * document at all. See that table's header.
 */

import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

/**
 * The document's shape version.
 *
 * Bump it when the stored shape changes in a way a reader cannot tolerate. Old documents
 * are then rejected by the guard below with a message naming the mismatch, rather than
 * being half-read into a view with missing fields — a frozen version that renders wrong
 * is worse than one that refuses to render, because only the second gets reported.
 */
export const PLAN_VERSION_SNAPSHOT_SCHEMA = 1;

/**
 * Narrow a `jsonb` column (Drizzle types it `unknown`) without an unguarded `as`.
 * Same predicate the Solution-delete audit test already uses; exported here because
 * `plan_approval.below_cost_lines` needs it too.
 */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export interface PlanVersionSnapshot {
  schema: number;
  /**
   * The plan exactly as it stood, cost included. `canEdit`, `canViewCost` and
   * `allowedTransitions` are stored as they were computed but are MEANINGLESS on read —
   * a version is read-only for everyone, and the reader's own cost grant decides what
   * they see. The shaper overrides all three.
   */
  view: ResourcePlanDetailView;
  /** When the freeze happened, ISO. RP-7.3's "Rate Card snapshot date" for this version. */
  frozenAt: string;
  /**
   * RP-8.1's "applied rates": the effective card rate per Labor Category on this plan's
   * Rate Basis at the freeze. Bill rates only — no cost — and the same map the live view
   * carries as `cardRates`, kept so a version can show what the card said versus what the
   * plan applied even after the card has moved on (RP-12.5).
   */
  cardRates: Record<string, string>;
  /**
   * The Account's negotiated rates (RP-12.4) in force at the freeze, per Labor Category.
   * Optional on read: versions frozen before this field existed have none, and that is a
   * fact about the version ("no Account override was ever resolved for it"), not a
   * corrupted document — see isPlanVersionSnapshot below.
   */
  accountOverrideRates?: Record<string, string>;
}

/**
 * Narrow a `jsonb` column into the document.
 *
 * This guard exists because `.claude/rules/code-quality.md` forbids `as X` at a data
 * boundary, and a `jsonb` read IS one. It deliberately checks the document's SPINE — the
 * envelope plus the arrays and scalars every reader dereferences — rather than
 * re-validating a payload this repo wrote itself field by field: a full structural
 * validator for `ResourcePlanDetailView` would be a second, silently-diverging definition
 * of the view, which is the drift this module exists to avoid.
 *
 * What it is really defending against is a schema-version mismatch and a corrupted row,
 * and it catches both.
 */
export function isPlanVersionSnapshot(value: unknown): value is PlanVersionSnapshot {
  if (!isRecord(value)) return false;
  if (value.schema !== PLAN_VERSION_SNAPSHOT_SCHEMA) return false;
  if (typeof value.frozenAt !== "string") return false;
  if (!isRecord(value.cardRates)) return false;
  // Optional: absent on any version frozen before this field existed (see the field's own
  // doc comment above) — only reject it when PRESENT but not a record.
  if (value.accountOverrideRates !== undefined && !isRecord(value.accountOverrideRates)) {
    return false;
  }

  const v = value.view;
  if (!isRecord(v)) return false;
  return (
    typeof v.id === "string" &&
    typeof v.name === "string" &&
    typeof v.status === "string" &&
    typeof v.startMonth === "string" &&
    typeof v.totalHours === "string" &&
    typeof v.indicativeRevenue === "string" &&
    typeof v.marginOfRecordKind === "string" &&
    Array.isArray(v.months) &&
    Array.isArray(v.lines) &&
    Array.isArray(v.workstreams) &&
    Array.isArray(v.byMonth)
  );
}

/** Every value of `plan_version_kind`. */
export const PLAN_VERSION_KINDS = ["Sent", "Approved", "Won", "Committed"] as const;

export type PlanVersionKind = (typeof PLAN_VERSION_KINDS)[number];

/** The kinds a lifecycle edge freezes — every kind but a planner's own commit. */
export type LifecycleVersionKind = Exclude<PlanVersionKind, "Committed">;

export function isPlanVersionKind(value: string): value is PlanVersionKind {
  return (PLAN_VERSION_KINDS as readonly string[]).includes(value);
}

/**
 * RP-8.1's "label" — generated, not typed.
 *
 * The versions panel has to be readable with no planner effort, and "v2 · Sent" is the
 * whole of what a reader needs to pick the right one. Nothing requires it to be unique;
 * the (plan, version, kind) unique index does that job in the storage layer.
 */
export function planVersionLabel(versionNumber: number, kind: PlanVersionKind): string {
  return `v${versionNumber} · ${kind}`;
}

/**
 * Which status a freeze of this kind lands the plan in.
 *
 * `Sent` and `Approved` both reach Proposed — they are the two paths through RP-17.1's
 * gate, not two destinations. Stored on the row rather than inferred on read so a later
 * slice that freezes on a new edge (M5's Handover, say) does not have to re-teach every
 * reader the mapping.
 */
export function planStatusForVersionKind(kind: LifecycleVersionKind): "Proposed" | "Won" {
  return kind === "Won" ? "Won" : "Proposed";
}

/** Longest "Commit version" label a planner may type. Matches the column's CHECK. */
export const COMMIT_LABEL_MAX_LENGTH = 120;

/**
 * Which version the live plan currently matches.
 *
 * The plan's own pointer when it has one (set by every commit and restore). A plan with no
 * pointer has never been committed or restored since versions could be — its newest
 * version is the live one, which is exactly what it was before this feature existed.
 * `versions` may be in any order; null when the plan has none.
 */
export function effectiveLiveVersionId(
  liveVersionId: string | null,
  versions: ReadonlyArray<{ id: string; frozenAt: Date | string; versionNumber: number }>,
): string | null {
  if (liveVersionId !== null && versions.some((v) => v.id === liveVersionId)) {
    return liveVersionId;
  }
  let newest: { id: string; at: number; versionNumber: number } | null = null;
  for (const v of versions) {
    const at = new Date(v.frozenAt).getTime();
    if (
      newest === null ||
      at > newest.at ||
      (at === newest.at && v.versionNumber > newest.versionNumber)
    ) {
      newest = { id: v.id, at, versionNumber: v.versionNumber };
    }
  }
  return newest?.id ?? null;
}

/** Why a version cannot be deleted, or null when it can. */
export type VersionDeleteRefusal = "system" | "notCommitter" | "live";

/**
 * Who may delete a version.
 *
 * Only a `Committed` version — the Sent, Approved and Won freezes are the record of what
 * the client saw and signed, and Change Order comparison and approvals point at them. Only
 * by the person who saved it. Never the live one. In any status — versions are saved in
 * every status, so they can be tidied in every status too.
 *
 * A committer whose account is gone leaves `committedByUserId` null, which matches nobody:
 * that version can no longer be deleted by anyone, admins included.
 */
export function versionDeleteRefusal(input: {
  kind: string;
  committedByUserId: string | null;
  actorUserId: string;
  isLive: boolean;
}): VersionDeleteRefusal | null {
  if (input.kind !== "Committed") return "system";
  if (input.committedByUserId === null || input.committedByUserId !== input.actorUserId) {
    return "notCommitter";
  }
  if (input.isLive) return "live";
  return null;
}

/**
 * Whether a reader may edit a plan — lines, rates, hours, Workstreams, Planned Pod, ramp,
 * Estimated Hours Lines, setup. Every status is editable (`canEdit` is the capability
 * alone); `canEditAfterWon` is kept as a fallback for a payload that predates that.
 */
export function canEditPlanContent(plan: { canEdit: boolean; canEditAfterWon?: boolean }): boolean {
  return plan.canEdit || plan.canEditAfterWon === true;
}
